import {useChatManager} from '@github-ui/copilot-chat/CopilotChatManagerContext'
import type {
  CopilotSpaceActorRole,
  IndexCopilotSpace,
  SpaceCollaborator,
  SpaceTeamCollaborator,
  SpaceUserCollaborator,
  SuggestedSpaceCollaborator,
  SuggestedSpaceTeamCollaborator,
  SuggestedSpaceUserCollaborator,
} from '@github-ui/copilot-chat-types'
import {GitHubAvatar} from '@github-ui/github-avatar'
import {ActionList, ActionMenu, Banner, Spinner, Stack, TextInput, Truncate} from '@primer/react'
import {useDeferredValue, useEffect, useRef, useState} from 'react'

import {useSpaceCollaborators} from '../hooks/use-copilot-space-collaborators'
import {useSuggestedSpaceCollaborators} from '../hooks/use-suggested-space-collaborators'
import styles from './SpaceCollaborators.module.css'

interface SpaceCollaboratorsProps {
  copilotSpace: IndexCopilotSpace
  onError: (errorMessages: Record<string, string>) => void
}

export function SpaceCollaborators({copilotSpace, onError}: SpaceCollaboratorsProps) {
  const manager = useChatManager()
  const [collaboratorInputValue, setCollaboratorInputValue] = useState('')
  const [showSuggestions, setShowSuggestions] = useState(false)
  const suggestionsRef = useRef<HTMLDivElement>(null)
  const deferredValue = useDeferredValue(
    collaboratorInputValue.startsWith('@') ? collaboratorInputValue.trim().slice(1) : collaboratorInputValue.trim(),
  )

  const {data: suggestedCollaborators, isLoading: suggestedCollaboratorsLoading} = useSuggestedSpaceCollaborators({
    input: {
      query: deferredValue,
      spaceOwner: copilotSpace.owner,
      spaceId: copilotSpace.id,
      allUsers: !copilotSpace.ownerIsOrg, // Use all_users=true for user-owned spaces
    },
  })

  const {
    collaborators,
    addCollaborator,
    error: collaboratorsError,
    isLoading: collaboratorsLoading,
  } = useSpaceCollaborators(copilotSpace.owner, copilotSpace.id)

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (suggestionsRef.current && !suggestionsRef.current.contains(event.target as Node)) {
        setShowSuggestions(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [])

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setShowSuggestions(
      Boolean(
        collaboratorInputValue &&
        !suggestedCollaboratorsLoading &&
        suggestedCollaborators &&
        suggestedCollaborators.length > 0,
      ),
    )
  }, [collaboratorInputValue, suggestedCollaborators, suggestedCollaboratorsLoading])

  async function handleSelectSuggestedCollaborator(item: SuggestedSpaceCollaborator) {
    const isDuplicate = collaborators?.some(
      collaborator => collaborator.actor_id === item.id && collaborator.actor_type === item.actor_type,
    )
    if (isDuplicate) {
      setCollaboratorInputValue('')
      setShowSuggestions(false)
      return
    }

    try {
      await addCollaborator({
        actor_id: item.id,

        role_name: 'custom_copilot_reader',

        actor_type: item.actor_type as 'user' | 'team',
      })
      setCollaboratorInputValue('')

      // Updating the collaborative flag if this is the first collaborator being added
      // to a user-owned space
      if (!copilotSpace.ownerIsOrg && collaborators && collaborators?.length <= 1) {
        manager.dispatch({
          type: 'SET_CUSTOM_COPILOT',
          partial: true,
          customCopilot: {
            ...copilotSpace,
            collaborative: true,
          },
        })
      }

      setShowSuggestions(false)
    } catch {
      onError({base: 'An error occurred while adding a collaborator.'})
    }
  }

  return (
    <>
      <Stack direction="vertical" gap="condensed" align="start">
        <span className="text-small text-bold color-fg-muted mb-1">People with access</span>
        <div className={styles.copyLinkSection} style={{position: 'relative'}}>
          <TextInput
            block
            placeholder={
              copilotSpace.ownerIsOrg ? `Search people within ${copilotSpace.owner}` : 'Search for any GitHub user'
            }
            value={collaboratorInputValue}
            onChange={e => setCollaboratorInputValue(e.target.value)}
            className="color-fg-default"
            aria-label={
              copilotSpace.ownerIsOrg
                ? 'Search for users or teams to share this space with'
                : 'Search for any GitHub user to share this space with'
            }
          />
          {showSuggestions && suggestedCollaborators && (
            <div ref={suggestionsRef}>
              <SpacesSuggestionsList items={suggestedCollaborators} onSelect={handleSelectSuggestedCollaborator} />
            </div>
          )}
        </div>
      </Stack>
      {collaboratorsLoading ? (
        <div className="d-flex flex-justify-center p-2">
          <Spinner />
        </div>
      ) : collaboratorsError ? (
        <Banner variant="critical" title="There was a problem with loading this Space's collaborators." />
      ) : collaborators && collaborators.length > 0 ? (
        <Stack direction="vertical" gap="condensed" align="start">
          {collaborators.map(actor => (
            <SpaceCollaboratorRow
              key={`${actor.actor_type}-${actor.actor_id}`}
              actor={actor}
              copilotSpace={copilotSpace}
              collaboratorsCount={collaborators.length}
              onError={onError}
            />
          ))}
        </Stack>
      ) : (
        <span className="text-small color-fg-muted p-2">{"This Space isn't shared with anyone yet"}</span>
      )}
    </>
  )
}

interface SpaceCollaboratorProps {
  actor: SpaceCollaborator
  copilotSpace: IndexCopilotSpace
  onError: (errorMessages: Record<string, string>) => void
  collaboratorsCount: number
}

function SpaceCollaboratorRow({actor, copilotSpace, onError, collaboratorsCount}: SpaceCollaboratorProps) {
  const actorIdentifier =
    actor.actor_type === 'user' ? (actor as SpaceUserCollaborator).display_login : (actor as SpaceTeamCollaborator).slug

  return (
    <div className="width-full d-flex flex-items-center flex-justify-between">
      <div className="d-flex flex-items-center mt-1">
        <GitHubAvatar src={actor.avatar_url} className="mr-2" square={actor.actor_type === 'team'} size={32} />
        <div className="d-flex flex-column mt-1">
          <span className="ml-1">
            <Truncate maxWidth="230px" title={actorIdentifier} inline>
              <b>{actorIdentifier}</b>
            </Truncate>
          </span>
          <span className="text-small color-fg-muted ml-1">
            <Truncate maxWidth="230px" title={actor.name} inline>
              <p>{actor.name}</p>
            </Truncate>
          </span>
        </div>
      </div>
      <ActorRoleSelect
        actor={actor}
        onError={onError}
        copilotSpace={copilotSpace}
        collaboratorsCount={collaboratorsCount}
      />
    </div>
  )
}

const ActorRoleOptions: Array<{text: string; value: CopilotSpaceActorRole}> = [
  {text: 'Admin', value: 'custom_copilot_admin'},
  {text: 'Editor', value: 'custom_copilot_writer'},
  {text: 'Viewer', value: 'custom_copilot_reader'},
]

const ActorRoleCaptions: Record<CopilotSpaceActorRole, string> = {
  custom_copilot_admin: 'Can view, make changes and add new people',

  custom_copilot_writer: 'Can view and make changes',

  custom_copilot_reader: 'Can view',
}

interface ActorRoleSelectProps {
  actor: SpaceCollaborator
  onError: (errorMessages: Record<string, string>) => void
  copilotSpace: IndexCopilotSpace
  collaboratorsCount: number
}

const MIN_COLLABORATORS_TO_BE_COLLABORATIVE = 2 // 1 (the creator counts as collaborator) + 1 (the collaborator)

function ActorRoleSelect({actor, onError, copilotSpace, collaboratorsCount}: ActorRoleSelectProps) {
  const manager = useChatManager()
  const [open, setOpen] = useState(false)

  const buttonRef = useRef(null)
  const currentBaseRole = ActorRoleOptions.find(opt => opt.value === actor.role)

  const {updateCollaborator, removeCollaborator, isUpdatePending, isRemovePending} = useSpaceCollaborators(
    copilotSpace.owner,
    copilotSpace.id,
  )

  async function updateActorRole(value: CopilotSpaceActorRole) {
    try {
      await updateCollaborator({
        actor_id: actor.actor_id,

        role_name: value,

        actor_type: actor.actor_type as 'user' | 'team',
      })
    } catch {
      onError({base: "An error occurred while updating an actor's permission level."})
    } finally {
      setOpen(false)
    }
  }

  async function removeActorRole() {
    try {
      await removeCollaborator({
        actor_id: actor.actor_id,

        actor_type: actor.actor_type as 'user' | 'team',
      })
    } catch {
      onError({base: "An error occurred while removing an actor's permissions."})
    } finally {
      // Updating the collaborative flag if this is the last collaborator being removed from a user-owned space
      if (!copilotSpace.ownerIsOrg && collaboratorsCount <= MIN_COLLABORATORS_TO_BE_COLLABORATIVE) {
        manager.dispatch({
          type: 'SET_CUSTOM_COPILOT',
          partial: true,
          customCopilot: {
            ...copilotSpace,
            collaborative: false,
          },
        })
      }

      setOpen(false)
    }
  }

  const actorRoleAriaLabel = currentBaseRole
    ? `Change role for ${actor.name} ${currentBaseRole.text} ${ActorRoleCaptions[currentBaseRole.value]}`
    : `Change role for ${actor.name}`

  return (
    <ActionMenu onOpenChange={setOpen} anchorRef={buttonRef} open={open}>
      <ActionMenu.Button
        disabled={actor.disabled}
        loading={isUpdatePending || isRemovePending}
        variant="invisible"
        className="text-small"
        ref={buttonRef}
        onClick={() => setOpen(true)}
        aria-label={actorRoleAriaLabel}
      >
        <Truncate title={currentBaseRole?.text || ''} inline>
          {currentBaseRole?.text}
        </Truncate>
      </ActionMenu.Button>
      <ActionMenu.Overlay side="outside-bottom" align="end" width="medium">
        <ActionList selectionVariant="single" aria-label={`Role options for ${actor.name}`}>
          {ActorRoleOptions.map(option => (
            <ActionList.Item
              selected={option.value === currentBaseRole?.value}
              key={option.value}
              onSelect={() => updateActorRole(option.value)}
            >
              <span className="text-medium">
                <b>{option.text}</b>
              </span>
              <span className="text-small color-fg-muted">
                <p className="m-0">{ActorRoleCaptions[option.value]}</p>
              </span>
            </ActionList.Item>
          ))}
          <ActionList.Divider />
          <ActionList.Item variant="danger" key={'remove'} onSelect={() => removeActorRole()}>
            <span className="text-medium">
              <b>{'Remove'}</b>
            </span>
            <span className="text-small color-fg-muted">
              <p className="m-0">{'Loses access to this content'}</p>
            </span>
          </ActionList.Item>
        </ActionList>
      </ActionMenu.Overlay>
    </ActionMenu>
  )
}

interface SuggestionsListProps {
  items: SuggestedSpaceCollaborator[]
  onSelect: (item: SuggestedSpaceCollaborator) => void
}

function SpacesSuggestionsList({items, onSelect}: SuggestionsListProps) {
  return (
    <div role="dialog" aria-label="Suggested collaborators" className={styles.spacesSuggestionsList}>
      <ActionList role="listbox">
        {items.map(item => {
          const key = `${item.actor_type}-${item.id}`
          const actorIdentifier =
            item.actor_type === 'user'
              ? (item as SuggestedSpaceUserCollaborator).login
              : (item as SuggestedSpaceTeamCollaborator).slug
          return (
            <ActionList.Item key={key} onSelect={() => onSelect(item)} role="option">
              <ActionList.LeadingVisual>
                <GitHubAvatar src={item.avatarUrl} square={item.actor_type === 'team'} size={20} />
              </ActionList.LeadingVisual>
              <span className="text-bold">{actorIdentifier}</span>
              {item.name && <ActionList.Description>{item.name}</ActionList.Description>}
            </ActionList.Item>
          )
        })}
      </ActionList>
    </div>
  )
}
